import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, ArrowUp, ArrowDown, MessageSquarePlus, Paperclip, Loader2, Square, Check, ChevronDown, BrainCircuit, GitBranch, Layers3, HelpCircle } from "lucide-react";
import ChatMessage, { TypingIndicator } from "@/components/ChatMessage";
import DeepAnalysisMessage from "@/components/DeepAnalysisMessage";
import HistoryDrawer from "@/components/HistoryDrawer";
import ProjectModal from "@/components/ProjectModal";
import ShareCardModal from "@/components/ShareCardModal";
import AttachmentChip from "@/components/AttachmentChip";
import SignupWallModal from "@/components/SignupWallModal";
import ImportHistoryModal from "@/components/ImportHistoryModal";
import ExportMenu from "@/components/ExportMenu";
import VoiceInputButton from "@/components/VoiceInputButton";
import { canExport } from "@/lib/exportAccess";
import { API, streamChat, uploadFile, SignupRequiredError, DailyLimitError, MonthlyTokenLimitError, fetchAnonQuota } from "@/lib/api";
import { isVoiceEnabledFor } from "@/lib/voice";
import { memoryApi } from "@/lib/memory";
import { historyApi } from "@/lib/historyApi";
import { projectsApi } from "@/lib/projectsApi";
import { useAuth } from "@/context/AuthContext";
import {
  loadConversations,
  upsertConversation,
  deleteConversation,
  clearConversations,
  makeTitle,
  newConversationId,
  wasImportedFor,
  markImportedFor,
  hasLocalConversations,
} from "@/lib/history";
import { toast } from "sonner";

const EXAMPLE_PROMPTS = [
  "How do I get my team to adopt a new process?",
  "Should I quit my stable job to follow my passion?",
  "How do I make new friends as an adult?",
  "How do I have a hard conversation I keep avoiding?",
  "I don't know what I want to do with my life.",
  "How do I stop comparing myself on social media?",
];

const MAX_FILE_BYTES = 20 * 1024 * 1024; // mirrors backend
const MAX_ATTACHMENTS = 5;
const stripStreamControlMarkers = (value = "") =>
  String(value).replace(/[\u200B\u2063\u2064]/g, "");

// Accept everything per user request. We let the backend classify.
const ACCEPT_ATTR =
  "image/*,audio/*,application/pdf,.docx,.txt,.md,.csv,.tsv,.json,.xml,.html,.htm,.yaml,.yml,.toml,.ini,.log,.py,.js,.jsx,.ts,.tsx,.css,.scss,.sh,.rb,.go,.rs,.java,.kt,.c,.cc,.cpp,.h,.hpp,.swift,.php,.sql,.r,.lua,.pl";

export default function Chat() {
  const { user, logout, setMemoryEnabled } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, content, attachments?, ts}[]
  const [input, setInput] = useState("");
  const [streamingText, setStreamingText] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | streaming | done | error
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // Attachment[] returned by /api/upload
  const [uploadingCount, setUploadingCount] = useState(0);
  // Chat mode: "standard" | "deep" | "extended". Toggled via dropdown next
  // to the chat input. Paid feature — free/anon users see an upsell
  // banner if they select it. When active, /api/chat/deep is used
  // instead of /api/chat; server streams a tree of 5 sub-Q&As followed
  // by a synthesis (see _deep_stream in server.py).
  const [chatMode, setChatMode] = useState("standard");
  const [deepUpsellOpen, setDeepUpsellOpen] = useState(false);
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  const modeMenuRef = useRef(null);

  const abortRef = useRef(null);
  const scrollerRef = useRef(null);
  // Tracks whether the user has scrolled up from the bottom of the
  // messages area. When true, we show a floating "jump to latest"
  // button so they don't have to hunt for the newest reply — small
  // but big UX win on long conversations where autoscroll can feel
  // like it "loses" the thread.
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const flushedSessionsRef = useRef(new Map());
  const sendClickGuardRef = useRef(0);
  const modeClickGuardRef = useRef(0);

  const [shareTarget, setShareTarget] = useState(null);
  const [anonQuota, setAnonQuota] = useState(null); // {used, limit, remaining, exceeded} or null
  const [signupWallOpen, setSignupWallOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);

  // Projects — folders + custom instructions. Admin + paid tier only.
  const [projects, setProjects] = useState([]);
  const [projectsAvailable, setProjectsAvailable] = useState(false);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [projectModalTarget, setProjectModalTarget] = useState(null); // null = create, obj = edit
  const [projectModalBusy, setProjectModalBusy] = useState(false);

  const isAnon = user === false;
  const isLoggedIn = user && typeof user === "object";
  const isAdmin = !!(user && user.role === "admin");
  const hasUnlimitedAccess = !!(user && (user.role === "admin" || user.limits?.unlimited));

  useEffect(() => {
    if (!isAdmin && chatMode === "extended") setChatMode("standard");
    if (!hasUnlimitedAccess && chatMode === "deep") setChatMode("standard");
  }, [chatMode, isAdmin, hasUnlimitedAccess]);

  // Pull current quota for anonymous visitors on mount and whenever auth flips.
  useEffect(() => {
    if (!isAnon) {
      setAnonQuota(null);
      return;
    }
    let cancelled = false;
    fetchAnonQuota()
      .then((q) => {
        if (cancelled) return;
        setAnonQuota(q);
        if (q.exceeded) setSignupWallOpen(true);
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, [isAnon]);

  useEffect(() => {
    document.title = "Humane Behavior AI";
  }, []);

  useEffect(() => {
    if (!input && inputRef.current) {
      inputRef.current.style.height = "28px";
    }
  }, [input]);

  useEffect(() => {
    if (!modeMenuOpen) return;
    const onDoc = (e) => {
      if (modeMenuRef.current && !modeMenuRef.current.contains(e.target)) {
        setModeMenuOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setModeMenuOpen(false);
    };
    document.addEventListener("pointerdown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("pointerdown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [modeMenuOpen]);

  // Load projects list for logged-in users. Free/non-paid users get
  // available:false and an empty list (upsell shown in the sidebar).
  useEffect(() => {
    if (!isLoggedIn) {
      setProjects([]);
      setProjectsAvailable(false);
      setActiveProjectId(null);
      return;
    }
    let cancelled = false;
    projectsApi
      .list()
      .then((r) => {
        if (cancelled) return;
        setProjects(r.projects || []);
        setProjectsAvailable(!!r.available);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const handleCreateProjectClick = () => {
    setProjectModalTarget(null);
    setProjectModalOpen(true);
  };
  const handleEditProjectClick = (proj) => {
    setProjectModalTarget(proj);
    setProjectModalOpen(true);
  };
  const handleDeleteProjectClick = async (proj) => {
    if (!window.confirm(`Delete project "${proj.name}"? Conversations inside will be moved out (not deleted).`)) return;
    try {
      await projectsApi.remove(proj.id);
      setProjects((prev) => prev.filter((p) => p.id !== proj.id));
      // Detach conversations locally so the UI reflects the server state.
      setConversations((prev) =>
        prev.map((c) => (c.projectId === proj.id ? { ...c, projectId: null } : c))
      );
      if (activeProjectId === proj.id) setActiveProjectId(null);
      toast.success("Project deleted.");
    } catch (e) {
      toast.error(e?.message || "Couldn't delete project.");
    }
  };
  const handleSaveProject = async ({ name, custom_instructions }) => {
    setProjectModalBusy(true);
    try {
      if (projectModalTarget) {
        const updated = await projectsApi.update(projectModalTarget.id, { name, custom_instructions });
        setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        toast.success("Project updated.");
      } else {
        const created = await projectsApi.create({ name, custom_instructions });
        setProjects((prev) => [created, ...prev]);
        setActiveProjectId(created.id);
        toast.success("Project created.");
      }
      setProjectModalOpen(false);
      setProjectModalTarget(null);
    } catch (e) {
      toast.error(e?.message || "Couldn't save project.");
    } finally {
      setProjectModalBusy(false);
    }
  };
  const handleSelectProject = (proj) => {
    setActiveProjectId(proj ? proj.id : null);
    // Optional: closing an active chat when switching project scope
    // keeps the empty state consistent with what you selected.
    if (proj) {
      // If current chat isn't in this project, drop it so user starts fresh.
      const current = conversations.find((c) => c.id === activeId);
      if (current && current.projectId !== proj.id) {
        setMessages([]);
        setActiveId(null);
      }
    }
  };

  // Load conversation list based on auth state. Anonymous → localStorage.
  // Logged-in → server. When a fresh sign-in happens and localStorage still
  // has conversations from the anonymous trial, offer a one-time import.
  useEffect(() => {
    if (user === null) return; // still resolving session
    if (isAnon) {
      setConversations(loadConversations());
      setImportModalOpen(false);
      return;
    }
    let cancelled = false;
    historyApi
      .list()
      .then((serverConvs) => {
        if (cancelled) return;
        const normalized = serverConvs.map((c) => ({
          id: c.id,
          title: c.title,
          messages: c.messages || [],
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
          projectId: c.projectId || null,
          kind: c.kind || "chat",
        }));
        setConversations(normalized);

        // Offer import if local storage has chats we haven't migrated yet
        if (
          isLoggedIn &&
          !wasImportedFor(user.id) &&
          hasLocalConversations()
        ) {
          setImportModalOpen(true);
        }
      })
      .catch(() => {
        // Server fetch failed — fall back to whatever we have locally so the
        // UI isn't empty. Don't surface; the chat itself still works.
        setConversations(loadConversations());
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (scrollerRef.current) {
      // Only auto-scroll to bottom if the user hasn't scrolled up.
      // Otherwise it feels like the app is fighting them — they
      // wanted to re-read, and we yanked them back down.
      if (!isScrolledUp) {
        scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      }
    }
  }, [messages, streamingText, status, pendingFiles, isScrolledUp]);

  // Track whether the scroller is near the bottom so we can toggle
  // the "jump to latest" button. Threshold is 80px — small enough
  // that a single scroll notch doesn't hide the button, big enough
  // that the button doesn't flicker on autoscroll bounce.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      setIsScrolledUp(!nearBottom);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [activeId]);

  const scrollToBottom = () => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setIsScrolledUp(false);
  };

  const isStreaming = status === "loading" || status === "streaming";
  const isBusy = isStreaming || uploadingCount > 0;

  // Best-effort: at the end of a thread OR after each completed exchange,
  // hand the conversation to the memory writer. Fire-and-forget. Backend
  // skips entirely if the thread touches any sensitive content, or if
  // the user has memory disabled. Debounced so rapid back-and-forth
  // doesn't hammer the extractor.
  //
  // flushedSessionsRef maps sessionId → last flushed message count so
  // subsequent turns re-flush (the old boolean flag caused only the
  // first exchange to ever be extracted, and every later turn's memory
  // was silently dropped).
  const flushDebounceRef = useRef(null);
  const flushMemoryFor = (sessionId, msgs, { debounce = false } = {}) => {
    if (!sessionId || !msgs || msgs.length === 0) return;
    if (!msgs.some((m) => m.role === "user")) return;
    if (!user || typeof user !== "object") return;
    const lastLen = flushedSessionsRef.current.get(sessionId) || 0;
    if (msgs.length <= lastLen) return;  // nothing new since last flush

    const doFlush = () => {
      flushedSessionsRef.current.set(sessionId, msgs.length);
      memoryApi.write(sessionId, msgs).catch(() => {
        // Roll back the counter so a later attempt can retry this exchange.
        flushedSessionsRef.current.set(sessionId, lastLen);
      });
    };

    if (debounce) {
      // Coalesce rapid successive turns (user replies quickly) — only run
      // the extractor after the user has been quiet for 3s.
      if (flushDebounceRef.current) clearTimeout(flushDebounceRef.current);
      flushDebounceRef.current = setTimeout(doFlush, 3000);
    } else {
      if (flushDebounceRef.current) {
        clearTimeout(flushDebounceRef.current);
        flushDebounceRef.current = null;
      }
      doFlush();
    }
  };

  // On tab close / hard refresh, try to flush the current thread.
  useEffect(() => {
    const handler = () => {
      if (user && user.memory_enabled && activeId && messages.length > 0) {
        flushMemoryFor(activeId, messages);
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [user, activeId, messages.length]);

  const startNewChat = () => {
    if (abortRef.current) abortRef.current.abort();
    if (user && user.memory_enabled && activeId && messages.length > 0) {
      flushMemoryFor(activeId, messages);
    }
    setActiveId(null);
    setMessages([]);
    setStreamingText("");
    setStatus("idle");
    setInput("");
    setPendingFiles([]);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const loadConversation = (conv) => {
    if (abortRef.current) abortRef.current.abort();
    if (user && user.memory_enabled && activeId && activeId !== conv.id && messages.length > 0) {
      flushMemoryFor(activeId, messages);
    }
    setActiveId(conv.id);
    setMessages(conv.messages || []);
    setStreamingText("");
    setStatus("idle");
    setPendingFiles([]);
    setDrawerOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleToggleMemory = async (next) => {
    try {
      await setMemoryEnabled(next);
      toast.success(next ? "Memory on" : "Memory off");
    } catch (err) {
      toast.error(err.message || "Could not update memory setting.");
    }
  };

  const onPickFiles = () => {
    if (isBusy) return;
    fileInputRef.current?.click();
  };

  const onFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-pick of the same file
    await handleIncomingFiles(files);
  };

  // Shared file-upload flow used by both the file picker and clipboard
  // paste. Extracted so the paste handler can reuse the exact same
  // size cap, slot cap, error toasts and upload logic. No functional
  // change vs the previous inline onFilesChosen — just refactored to
  // accept a plain File[] array.
  const handleIncomingFiles = async (files) => {
    if (!files || !files.length) return;

    const slots = MAX_ATTACHMENTS - pendingFiles.length;
    if (slots <= 0) {
      toast.error(`Up to ${MAX_ATTACHMENTS} files per message.`);
      return;
    }
    const toUpload = files.slice(0, slots).filter((f) => {
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`${f.name} is over 20 MB — skipped.`);
        return false;
      }
      return true;
    });
    if (!toUpload.length) return;

    setUploadingCount((n) => n + toUpload.length);
    await Promise.all(
      toUpload.map(async (file) => {
        try {
          const att = await uploadFile(file);
          setPendingFiles((prev) => [...prev, att]);
        } catch (err) {
          console.error(err);
          toast.error(`Couldn't attach ${file.name}`);
        } finally {
          setUploadingCount((n) => Math.max(0, n - 1));
        }
      })
    );
  };

  // Clipboard paste handler. Three cases we intercept:
  //   1. Screenshots / images copied from anywhere (Cmd+Shift+4, Snip,
  //      Preview, browser image copy, etc.) — clipboard has a "file"
  //      item with type image/*.
  //   2. Files copied in the OS file manager (PDF, DOCX, TXT, mp3…)
  //      — same "file" item pathway with the appropriate MIME.
  //   3. Long text pasted from a document (Word, PDF selection, web
  //      article) — comes in as text/plain. If it's >4 KB we treat it
  //      as a document paste and convert it into a .txt attachment so
  //      the backend can chunk & summarize it instead of drowning the
  //      textarea. Under 4 KB we let the native paste happen so short
  //      quotes/URLs still land in the input as text.
  // Anything we DON'T intercept falls through to the browser's default
  // paste (inserts into the textarea).
  const onPasteInput = async (e) => {
    if (isBusy) return;
    const cd = e.clipboardData;
    if (!cd) return;

    const files = [];
    for (const item of Array.from(cd.items || [])) {
      if (item.kind === "file") {
        const f = item.getAsFile();
        if (f) files.push(f);
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      // Give pasted screenshots a friendly name (Chrome hands them over
      // as literally "image.png" which is fine, but stamping a timestamp
      // helps the user distinguish multiple pastes in one turn).
      const stamped = files.map((f, i) => {
        if (f.name && f.name !== "image.png") return f;
        const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
        const ext = (f.type && f.type.split("/")[1]) || "png";
        return new File([f], `pasted-${ts}-${i + 1}.${ext}`, { type: f.type || "image/png" });
      });
      await handleIncomingFiles(stamped);
      toast.success(stamped.length === 1 ? "Pasted 1 attachment" : `Pasted ${stamped.length} attachments`);
      return;
    }

    // No files. Check for a long text paste — treat as document.
    const text = cd.getData && cd.getData("text/plain");
    if (text && text.length > 4000) {
      e.preventDefault();
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const firstLine = text.split("\n")[0].slice(0, 40).replace(/[^\w\s.-]/g, "").trim() || "clipboard";
      const filename = `pasted-${firstLine.replace(/\s+/g, "_")}-${ts}.txt`;
      const blob = new File([text], filename, { type: "text/plain" });
      await handleIncomingFiles([blob]);
      toast.success(`Pasted ${(text.length / 1000).toFixed(1)} KB of text as ${filename}`);
      return;
    }
    // Small text paste → let it drop into the textarea normally.
  };

  const removePendingFile = (idx) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // Edit a previously-sent user message. Drops every message at and after
  // that index, restores the input + attachments, and refocuses. The user
  // re-sends to get a fresh assistant reply.
  const handleEditMessage = (idx) => {
    if (isBusy) return;
    const target = messages[idx];
    if (!target || target.role !== "user") return;
    if (abortRef.current) abortRef.current.abort();
    setMessages(messages.slice(0, idx));
    setStreamingText("");
    setStatus("idle");
    setInput(target.content || "");
    setPendingFiles(target.attachments ? [...target.attachments] : []);
    setTimeout(() => {
      inputRef.current?.focus();
      if (inputRef.current) {
        inputRef.current.style.height = "auto";
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 160) + "px";
      }
    }, 0);
  };

  // Stop the in-flight stream. The AbortError branch in send() preserves any
  // partial reply already received so the user doesn't lose context.
  const handleStop = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  // One-tap resume after the backend exhausted its auto-continuation budget
  // and surfaced an "ask in tranches" notice. The offer shape is
  // { next, isNumbered } — if numbered, we prefill "continue from N";
  // otherwise just "continue from where you stopped". We submit immediately.
  const handleContinue = (offer) => {
    if (isBusy) return;
    const text =
      offer && offer.isNumbered && offer.next
        ? `Continue from ${offer.next}. Do not repeat earlier items.`
        : `Continue from where you stopped. Do not repeat earlier text.`;
    send(text);
  };

  // Deep Analysis send path. Uses /api/chat/deep which emits an
  // event-stream (custom SSE-like protocol) with:
  //   <event type="subquestions">{"questions":[...]}</event>
  //   <event type="answer" i="N">{"question":"...","answer":"..."}</event>
  //   <event type="synthesis-start"></event>
  //   ...synthesis text chunks...
  //   <event type="done"></event>
  // The assistant message is built up progressively in a `deepTree`
  // structure so ChatMessage can render each sub-Q as it completes,
  // followed by the synthesis at the bottom.
  const sendDeep = async (prompt) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const nowIso = new Date().toISOString();
    const userMsg = { role: "user", content: prompt, ts: nowIso };
    let currentDeep = { stage: "planning", subs: [], synthesis: "" };
    const asstMsg = {
      role: "assistant",
      content: "",
      ts: nowIso,
      deep: currentDeep,
    };
    const nextMessages = [...messages, userMsg, asstMsg];

    let conversationId = activeId;
    if (!conversationId) {
      conversationId = newConversationId();
      setActiveId(conversationId);
    }

    setMessages(nextMessages);
    setInput("");
    setStatus("streaming");
    const asstIdx = nextMessages.length - 1;
    const updateAsst = (patch) => {
      currentDeep = { ...currentDeep, ...patch };
      setMessages((prev) =>
        prev.map((m, i) => (i === asstIdx ? { ...m, deep: { ...m.deep, ...patch } } : m))
      );
    };

    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/deep`, {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, session_id: conversationId }),
      });
      if (resp.status === 402) {
        setDeepUpsellOpen(true);
        setMessages((prev) => prev.slice(0, -2)); // roll back the two placeholder msgs
        setStatus("idle");
        return;
      }
      if (!resp.ok) throw new Error(`deep chat http ${resp.status}`);

      const reader = resp.body.getReader();
      const dec = new TextDecoder("utf-8");
      let buf = "";
      let synthStarted = false;
      let synthText = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        // Consume complete <event ...>...</event>\n blocks first
        while (true) {
          const evStart = buf.indexOf("<event ");
          if (evStart < 0) break;
          const evEnd = buf.indexOf("</event>\n", evStart);
          if (evEnd < 0) break;
          // If there's synthesis text BEFORE evStart AND synth has started, capture it
          if (synthStarted && evStart > 0) {
            synthText += buf.slice(0, evStart);
            updateAsst({ synthesis: synthText });
          }
          const evBlock = buf.slice(evStart, evEnd);
          buf = buf.slice(evEnd + "</event>\n".length);
          const typeMatch = /type="([^"]+)"/.exec(evBlock);
          const iMatch = /\bi="(\d+)"/.exec(evBlock);
          const payloadStart = evBlock.indexOf(">") + 1;
          const payloadStr = evBlock.slice(payloadStart).trim();
          const evType = typeMatch?.[1];
          if (evType === "subquestions") {
            const { questions } = JSON.parse(payloadStr);
            updateAsst({
              stage: "answering",
              subs: questions.map((q) => ({ question: q, answer: "", done: false })),
            });
          } else if (evType === "answer") {
            const idx = Number(iMatch?.[1] ?? 0);
            const { question, answer } = JSON.parse(payloadStr);
            currentDeep = {
              ...currentDeep,
              subs: (currentDeep.subs || []).map((s, j) =>
                j === idx ? { question, answer, done: true } : s
              ),
            };
            setMessages((prev) =>
              prev.map((m, i) =>
                i === asstIdx
                  ? {
                      ...m,
                      deep: {
                        ...m.deep,
                        subs: m.deep.subs.map((s, j) =>
                          j === idx ? { question, answer, done: true } : s
                        ),
                      },
                    }
                  : m
              )
            );
          } else if (evType === "synthesis-start") {
            synthStarted = true;
            updateAsst({ stage: "synthesizing", synthesis: "" });
          } else if (evType === "done") {
            updateAsst({ stage: "done" });
          } else if (evType === "error") {
            const { message } = JSON.parse(payloadStr);
            updateAsst({ stage: "error", error: message });
          }
        }
        // Anything left in buf that isn't an <event ...> chunk while synth
        // is streaming is real synthesis content.
        if (synthStarted && buf.length > 0 && !buf.includes("<event ")) {
          synthText += buf;
          buf = "";
          updateAsst({ synthesis: synthText });
        }
      }
      setStatus("done");
      const finalContent = synthText;
      const completedAssistant = {
        ...asstMsg,
        content: finalContent,
        deep: { ...currentDeep, stage: currentDeep.stage === "error" ? "error" : "done", synthesis: finalContent },
      };
      const finalMessages = [...nextMessages.slice(0, -1), completedAssistant];
      setMessages(finalMessages);

      const existing = conversations.find((c) => c.id === conversationId);
      const conv = {
        id: conversationId,
        title: existing?.title || makeTitle(userMsg.content),
        messages: finalMessages,
        createdAt: existing?.createdAt || nowIso,
        updatedAt: new Date().toISOString(),
        projectId: existing?.projectId ?? (activeProjectId || null),
      };
      if (isLoggedIn) {
        setConversations((prev) => {
          const idx = prev.findIndex((c) => c.id === conv.id);
          const next = idx >= 0 ? [...prev] : [conv, ...prev];
          if (idx >= 0) next[idx] = conv;
          next.sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
          return next;
        });
        historyApi.upsert(conv).catch((err) => console.warn("deep history.upsert failed", err));
      } else {
        try {
          setConversations(upsertConversation(conv));
        } catch (e) {
          console.warn("deep history save failed", e);
        }
      }
      if (user && user.memory_enabled) {
        flushMemoryFor(conversationId, finalMessages, { debounce: true });
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("deep chat failed:", err);
        updateAsst({ stage: "error", error: err.message });
      }
      setStatus("idle");
    }
  };

  const sendExtended = async (prompt) => {
    if (!isAdmin) {
      toast.error("Extended Thinking is admin-only.");
      setChatMode("standard");
      return;
    }
    if (pendingFiles.length > 0) {
      toast.error("Extended Thinking currently accepts text only. Remove attachments or use Standard mode.");
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const nowIso = new Date().toISOString();
    const userMsg = { role: "user", content: prompt, ts: nowIso };
    const nextMessages = [...messages, userMsg];

    let conversationId = activeId;
    if (!conversationId) {
      conversationId = newConversationId();
      setActiveId(conversationId);
    }

    setMessages(nextMessages);
    setInput("");
    setStreamingText("");
    setStatus("loading");

    try {
      const res = await fetch(`${API}/admin/dynamic-hbm/evaluate`, {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversation_id: conversationId,
          message: prompt,
          mode: "dynamic",
          reset_state: false,
          debug: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Extended Thinking failed (${res.status})`);
      if (data.dynamic?.error) throw new Error(data.dynamic.error);
      const result = data.dynamic || {};
      const output = result.controller_output;
      const answer = result.final_response || output?.final_response || "Extended Thinking returned no answer.";
      const assistantMsg = {
        role: "assistant",
        content: answer,
        ts: new Date().toISOString(),
        dynamicHbm: output,
      };
      const finalMessages = [...nextMessages, assistantMsg];
      setMessages(finalMessages);
      setStatus("done");

      const conv = {
        id: conversationId,
        title: conversations.find((c) => c.id === conversationId)?.title || makeTitle(userMsg.content),
        messages: finalMessages,
        createdAt: conversations.find((c) => c.id === conversationId)?.createdAt || nowIso,
        updatedAt: new Date().toISOString(),
        projectId: activeProjectId || null,
      };
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === conv.id);
        const next = idx >= 0 ? [...prev] : [conv, ...prev];
        if (idx >= 0) next[idx] = conv;
        next.sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
        return next;
      });
      historyApi.upsert(conv).catch(() => {});
    } catch (err) {
      if (err.name === "AbortError") {
        setMessages(messages);
        setInput(prompt);
        setStatus("idle");
        return;
      }
      setMessages(messages);
      setInput(prompt);
      setStatus("error");
      toast.error(err.message || "Extended Thinking failed.");
    }
  };

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    const hasAttachments = pendingFiles.length > 0;
    if ((!trimmed && !hasAttachments) || isBusy) return;

    // Deep Analysis mode gate — currently admin-only. Non-admins see the
    // access modal instead of hitting the endpoint (server would 402 anyway).
    if (chatMode === "deep") {
      const canUseDeep = hasUnlimitedAccess;
      if (!canUseDeep) {
        setDeepUpsellOpen(true);
        return;
      }
      // Deep Analysis path — different endpoint, different stream format.
      return sendDeep(trimmed);
    }

    if (chatMode === "extended") {
      return sendExtended(trimmed);
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const nowIso = new Date().toISOString();
    const userMsg = {
      role: "user",
      content: trimmed,
      ts: nowIso,
      ...(hasAttachments ? { attachments: pendingFiles } : {}),
    };
    const nextMessages = [...messages, userMsg];

    let conversationId = activeId;
    if (!conversationId) {
      conversationId = newConversationId();
      setActiveId(conversationId);
    }

    setMessages(nextMessages);
    setInput("");
    setPendingFiles([]);
    setStreamingText("");
    setStatus("loading");

    let assembled = "";
    let partial = "";
    try {
      assembled = await streamChat({
        messages: nextMessages.map((m) => ({
          role: m.role,
          content: m.content,
          ...(m.attachments && m.attachments.length ? { attachments: m.attachments } : {}),
        })),
        sessionId: conversationId,
        projectId: activeProjectId || null,
        signal: controller.signal,
        onChunk: (chunk) => {
          const visibleChunk = stripStreamControlMarkers(chunk);
          if (!visibleChunk) return;
          partial += visibleChunk;
          setStatus("streaming");
          setStreamingText((prev) => prev + visibleChunk);
        },
      });
      assembled = stripStreamControlMarkers(assembled || partial);
    } catch (err) {
      if (err.name === "AbortError") {
        // User clicked Stop. Keep whatever partial reply we got.
        const saved = stripStreamControlMarkers(partial || "").trim();
        if (saved) {
          const assistantMsg = {
            role: "assistant",
            content: saved,
            ts: new Date().toISOString(),
            stopped: true,
          };
          setMessages([...nextMessages, assistantMsg]);
        } else {
          // No partial — roll back so the user can retry without their text gone.
          setMessages(messages);
          setInput(trimmed);
          if (hasAttachments) setPendingFiles(pendingFiles);
        }
        setStreamingText("");
        setStatus("idle");
        return;
      }
      if (err instanceof DailyLimitError) {
        setStatus("idle");
        // Roll back the optimistic user message so the input is editable.
        setMessages(messages);
        setInput(trimmed);
        if (hasAttachments) setPendingFiles(pendingFiles);
        setStreamingText("");
        const used = err.detail?.used ?? 10;
        const limit = err.detail?.limit ?? 10;
        toast.error(
          `Daily limit reached (${used}/${limit}). Come back after UTC midnight — your counter resets then.`,
          { duration: 8000 }
        );
        return;
      }
      if (err instanceof MonthlyTokenLimitError) {
        setStatus("idle");
        setMessages(messages);
        setInput(trimmed);
        if (hasAttachments) setPendingFiles(pendingFiles);
        setStreamingText("");
        toast.error(
          "You've reached your monthly usage limit. Your account resets on the 1st (UTC).",
          { duration: 10000 }
        );
        return;
      }
      if (err instanceof SignupRequiredError) {
        setStatus("idle");
        // Roll back the optimistic user message so the input becomes editable again.
        setMessages(messages);
        setInput(trimmed);
        if (hasAttachments) setPendingFiles(pendingFiles);
        setStreamingText("");
        setAnonQuota({
          used: err.detail?.used ?? 5,
          limit: err.detail?.limit ?? 5,
          remaining: 0,
          exceeded: true,
        });
        setSignupWallOpen(true);
        return;
      }
      // Real failure (network, 500, Gemini+Claude both down). If we already
      // streamed some content, preserve it as a (stopped) assistant message
      // — the user saw it appear and shouldn't have it ripped away. Only
      // restore the input if we have nothing to keep.
      setStatus("error");
      setStreamingText("");
      const saved = stripStreamControlMarkers(partial || "").trim();
      const msg = (err && err.message) || "";
      const looksNetwork =
        msg.includes("Failed to fetch") || msg.toLowerCase().includes("network");
      if (saved) {
        const assistantMsg = {
          role: "assistant",
          content: saved,
          ts: new Date().toISOString(),
          stopped: true,
        };
        setMessages([...nextMessages, assistantMsg]);
        if (looksNetwork) {
          toast.error("Connection dropped mid-reply. Ask 'continue' to resume.");
        } else if (msg.includes("(5")) {
          toast.error("Our model is overloaded right now. Try again in a few seconds.");
        } else {
          toast.error(msg || "Stream ended early — ask 'continue' to resume.");
        }
        return;
      }
      setMessages(messages);
      setInput(trimmed);
      if (hasAttachments) setPendingFiles(pendingFiles);
      if (looksNetwork) {
        toast.error("Network hiccup — your message is still in the box. Try again.");
      } else if (msg.includes("(5")) {
        toast.error("Our model is overloaded right now. Try again in a few seconds.");
      } else {
        toast.error(msg || "Something went wrong — try again.");
      }
      return;
    }

    // Successful turn — refresh anon quota for the indicator.
    if (isAnon) {
      fetchAnonQuota()
        .then((q) => {
          setAnonQuota(q);
          if (q.exceeded) setSignupWallOpen(true);
        })
        .catch(() => {});
    }

    const assistantMsg = {
      role: "assistant",
      content: stripStreamControlMarkers(assembled).trim(),
      ts: new Date().toISOString(),
    };
    const finalMessages = [...nextMessages, assistantMsg];

    setMessages(finalMessages);
    setStreamingText("");
    setStatus("done");

    // Persist. Strip large data_url blobs from attachments before saving to
    // localStorage so we don't blow the 5MB quota.
    const slimMessages = finalMessages.map((m) => {
      if (!m.attachments) return m;
      return {
        ...m,
        attachments: m.attachments.map((a) => ({
          kind: a.kind,
          filename: a.filename,
          mime: a.mime,
          size: a.size,
          storage_path: a.storage_path,
          // keep small previews only
          ...(a.kind === "image" && a.data_url && a.size && a.size < 200_000
            ? { data_url: a.data_url }
            : {}),
        })),
      };
    });

    const existing = conversations.find((c) => c.id === conversationId);
    const conv = {
      id: conversationId,
      title: existing?.title || makeTitle(userMsg.content || pendingFiles[0]?.filename || "Attachment"),
      messages: slimMessages,
      createdAt: existing?.createdAt || nowIso,
      updatedAt: new Date().toISOString(),
      projectId: existing?.projectId ?? (activeProjectId || null),
    };
    if (isLoggedIn) {
      // Optimistic local merge + server upsert. Failure is non-fatal.
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === conv.id);
        const next = idx >= 0 ? [...prev] : [conv, ...prev];
        if (idx >= 0) next[idx] = conv;
        next.sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""));
        return next;
      });
      historyApi.upsert(conv).catch((err) => {
        console.warn("history.upsert failed", err);
      });
      // Auto-title: on the FIRST assistant reply in a fresh conversation
      // (no existing title beyond the placeholder from `makeTitle`),
      // ask the backend to synthesize a short LLM title from the
      // question + answer. Best-effort; falls back to the placeholder.
      const wasFreshTurn = !existing;
      if (wasFreshTurn && (userMsg.content || "").trim()) {
        projectsApi
          .generateTitle({
            user_message: userMsg.content || "",
            assistant_message: assembled.trim(),
          })
          .then((r) => {
            const newTitle = (r?.title || "").trim();
            if (!newTitle) return;
            const updated = { ...conv, title: newTitle, updatedAt: new Date().toISOString() };
            setConversations((prev) =>
              prev.map((c) => (c.id === conv.id ? updated : c))
            );
            historyApi.upsert(updated).catch(() => {});
          })
          .catch(() => {
            /* non-fatal — placeholder title stays */
          });
      }
      // Fire the memory extractor 3s after each completed reply so
      // stable facts about the user + the people they've mentioned get
      // persisted across sessions. Debounced so rapid follow-up turns
      // coalesce into a single extraction pass. beforeunload is unreliable
      // on mobile — this is the load-bearing flush point.
      if (user && user.memory_enabled) {
        flushMemoryFor(conversationId, finalMessages, { debounce: true });
      }
    } else {
      try {
        setConversations(upsertConversation(conv));
      } catch (e) {
        console.warn("history save failed", e);
      }
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const now = Date.now();
    if (now - sendClickGuardRef.current < 350) return;
    sendClickGuardRef.current = now;
    send();
  };

  const handleModeToggle = (e) => {
    e.preventDefault();
    const now = Date.now();
    if (now - modeClickGuardRef.current < 180) return;
    modeClickGuardRef.current = now;
    setModeMenuOpen((v) => !v);
  };

  const handleModeSelect = (value) => (e) => {
    e.preventDefault();
    setChatMode(value);
    setModeMenuOpen(false);
  };

  const onKeyDown = (e) => {
    // Send only on a "pure Enter":
    //   - Not on touch-primary devices (mobile/tablet). On phones the
    //     on-screen ↵ key is a "new line" affordance, not a submit —
    //     matches every messaging app people already use. Sending on
    //     mobile happens only via the Send button.
    //   - No modifier keys (Shift/Alt/Ctrl/Cmd → newline instead)
    //   - Not during IME composition (Chinese/Japanese/Korean/etc.
    //     keyboards fire Enter when confirming a candidate — we must
    //     NOT send in that case). `isComposing` + keyCode 229 covers
    //     both modern and legacy browsers.
    //   - Space and every other key are ignored — send() only ever
    //     fires on the exact Enter event.
    if (e.key !== "Enter") return;
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(pointer: coarse)").matches
    ) {
      return; // mobile/touch: let Enter insert a newline
    }
    if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.nativeEvent?.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    send();
  };

  const onDeleteConv = (id) => {
    if (isLoggedIn) {
      setConversations((prev) => prev.filter((c) => c.id !== id));
      historyApi.remove(id).catch(() => {});
    } else {
      setConversations(deleteConversation(id));
    }
    if (id === activeId) startNewChat();
  };

  const onClearAll = () => {
    if (isLoggedIn) {
      setConversations([]);
      historyApi.clear().catch(() => {});
    } else {
      setConversations(clearConversations());
    }
    startNewChat();
    toast.success("History cleared");
  };

  const handleImportConfirm = async () => {
    if (!isLoggedIn || importBusy) return;
    setImportBusy(true);
    const local = loadConversations();
    try {
      const result = await historyApi.importBulk(local);
      markImportedFor(user.id);
      // Clear local copy now that they live server-side.
      clearConversations();
      // Re-fetch the merged list.
      const serverConvs = await historyApi.list();
      setConversations(
        serverConvs.map((c) => ({
          id: c.id,
          title: c.title,
          messages: c.messages || [],
          createdAt: c.createdAt,
          updatedAt: c.updatedAt,
        }))
      );
      toast.success(`Imported ${result.imported} previous ${result.imported === 1 ? "chat" : "chats"}.`);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't import previous chats. They're still in this browser.");
    } finally {
      setImportBusy(false);
      setImportModalOpen(false);
    }
  };

  const handleImportSkip = () => {
    if (importBusy) return;
    if (isLoggedIn) markImportedFor(user.id);
    setImportModalOpen(false);
  };

  const questionForAnswer = (assistantIndex) => {
    for (let i = assistantIndex - 1; i >= 0; i--) {
      if (messages[i]?.role === "user") return messages[i].content;
    }
    return "";
  };

  const handleShare = (assistantContent, assistantIndex) => {
    setShareTarget({
      question: questionForAnswer(assistantIndex),
      answer: assistantContent,
    });
  };

  const showEmptyState = messages.length === 0 && status === "idle";
  const canSend = (input.trim() || pendingFiles.length > 0) && !isBusy;
  const modeOptions = [
    { value: "standard", label: "Standard", shortLabel: "Standard", description: "Fast direct HBM reply", icon: MessageSquarePlus },
    ...(hasUnlimitedAccess ? [{ value: "deep", label: "Deep Analysis", shortLabel: "Deep", description: "Five-question analysis tree", icon: Layers3 }] : []),
    ...(isAdmin ? [{ value: "extended", label: "Extended Thinking", shortLabel: "Extended", description: "Dynamic HBM state guide", icon: BrainCircuit }] : []),
  ];
  const activeMode = modeOptions.find((opt) => opt.value === chatMode) || modeOptions[0];
  const ActiveModeIcon = activeMode.icon;

  return (
    <div className="h-screen min-h-[100dvh] w-full flex overflow-hidden bg-[var(--bg)]">
      {/* Persistent sidebar on lg+ */}
      <div className="hidden lg:flex shrink-0" data-testid="history-sidebar-wrap">
        <HistoryDrawer
          pinned
          open
          onClose={() => {}}
          conversations={conversations}
          activeId={activeId}
          onSelect={loadConversation}
          onDelete={onDeleteConv}
          onClear={onClearAll}
          onNew={startNewChat}
          user={user}
          onLogout={logout}
          memoryEnabled={!!(user && user.memory_enabled)}
          onToggleMemory={handleToggleMemory}
          projects={projects}
          projectsAvailable={projectsAvailable}
          activeProjectId={activeProjectId}
          onSelectProject={handleSelectProject}
          onCreateProject={handleCreateProjectClick}
          onEditProject={handleEditProjectClick}
          onDeleteProject={handleDeleteProjectClick}
        />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden relative">
        <div className="noise-overlay" aria-hidden="true" />

        {/* Header */}
        <header
          className="shrink-0 border-b border-[var(--border)] bg-[var(--bg)] z-40 relative"
          data-testid="app-header"
        >
        <div className="px-3 sm:px-4 md:px-6 py-3 flex items-center gap-2 sm:gap-4">
          <button
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden h-9 px-3 rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors inline-flex items-center gap-2 shrink-0"
            data-testid="history-toggle"
            aria-label="Open history"
          >
            <Menu size={14} />
            <span>Menu</span>
          </button>

          <div className="flex-1 text-center min-w-0">
            <h1 className="font-mono-ui text-[10px] sm:text-[11px] md:text-xs tracking-[0.22em] sm:tracking-[0.4em] uppercase text-[var(--accent)] inline-flex items-center justify-center gap-1.5 sm:gap-2 max-w-full">
              <span className="truncate">
                <span className="sm:hidden">HBM AI</span>
                <span className="hidden sm:inline">Humane Behavior AI</span>
              </span>
              <span
                data-testid="beta-badge"
                className="hidden min-[380px]:inline font-mono-ui text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border border-[var(--accent)] text-[var(--accent)] rounded-sm shrink-0"
              >
                Beta
              </span>
              {isAnon && anonQuota && !anonQuota.exceeded && (
                <span
                  data-testid="anon-quota-badge"
                  className="font-mono-ui text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border border-[var(--border)] text-[var(--text-muted)] rounded-sm"
                  title={`${anonQuota.remaining} of ${anonQuota.limit} free queries left`}
                >
                  {anonQuota.remaining}/{anonQuota.limit} free
                </span>
              )}
            </h1>
          </div>

          <div className="flex items-center gap-2 md:gap-5 shrink-0">
            {isAdmin && (
              <Link
                to="/hbm"
                className="hidden md:inline-flex h-9 px-3 rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors items-center justify-center"
                data-testid="header-hbm-socials-link"
                title="Open HBM Socials dashboards"
              >
                HBM Socials
              </Link>
            )}
            {isAnon && (
              <div
                className="flex items-center gap-2"
                data-testid="header-auth-actions"
              >
                <Link
                  to="/login"
                  className="h-9 px-3 rounded-full border border-[var(--accent)] bg-[var(--accent)] font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--bg)] hover:bg-transparent hover:text-[var(--accent)] transition-colors inline-flex items-center justify-center"
                  data-testid="header-sign-in-link"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="hidden sm:inline-flex h-9 px-3 rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors items-center justify-center"
                  data-testid="header-create-account-link"
                >
                  Create
                </Link>
              </div>
            )}
            {messages.length > 0 && !isStreaming && canExport(user) && (
              <ExportMenu
                conversation={{
                  id: activeId || "current",
                  title: conversations.find((c) => c.id === activeId)?.title || (messages[0]?.content || "Conversation").slice(0, 60),
                  messages,
                  createdAt: conversations.find((c) => c.id === activeId)?.createdAt,
                  updatedAt: new Date().toISOString(),
                }}
                anonymous={false}
                testIdPrefix="chat-export"
                size="md"
              />
            )}
            <button
              onClick={startNewChat}
              disabled={isStreaming}
              className="h-9 w-9 sm:w-auto sm:px-3 rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="new-chat"
              aria-label="New chat"
            >
              <MessageSquarePlus size={13} />
              <span className="hidden sm:inline">New</span>
            </button>
          </div>
        </div>
      </header>

      {/* Conversation area */}
      <main
        ref={scrollerRef}
        className="flex-1 overflow-y-auto scroll-warm relative"
        data-testid="chat-main"
      >
        {/* Floating "jump to latest" — only visible when user has
            scrolled up from the bottom. One-tap way to catch up on
            the newest reply without hunting. */}
        {isScrolledUp && messages.length > 0 && (
          <button
            type="button"
            onClick={scrollToBottom}
            data-testid="chat-scroll-to-bottom"
            aria-label="Jump to latest message"
            title="Jump to latest"
            className="fixed bottom-32 md:bottom-36 right-6 md:right-10 z-30 w-10 h-10 rounded-full border border-[var(--border)] bg-[var(--bg-elevated)] shadow-lg text-[var(--text)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors flex items-center justify-center"
          >
            <ArrowDown size={16} />
          </button>
        )}
        <div className="max-w-[760px] mx-auto px-5 md:px-8 py-8 md:py-12">
          {showEmptyState ? (
            <div className="text-center pt-16 md:pt-24" data-testid="empty-state">
              <h2 className="font-prose text-3xl md:text-5xl italic text-[var(--text)] leading-tight" data-testid="empty-state-title">
                Ask something a textbook
                <br />
                would lie to you about.
              </h2>
              <p className="font-ui text-sm md:text-base text-[var(--text-muted)] mt-6 max-w-md mx-auto">
                Not the advice that assumes you&apos;re rested, motivated, and free of obligations. The version that accounts for being a person.
              </p>

              <div className="mt-12">
                <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-dim)] mb-4">
                  try
                </div>
                <ul className="flex flex-col items-stretch gap-2 max-w-[480px] mx-auto">
                  {EXAMPLE_PROMPTS.map((q, i) => (
                    <li key={i}>
                      <button
                        onClick={() => send(q)}
                        className="w-full text-left px-4 py-3 border border-[var(--border-soft)] hover:border-[var(--accent)] font-ui text-sm md:text-[15px] text-[var(--text-muted)] hover:text-[var(--text)] transition-colors rounded"
                        data-testid={`example-prompt-${i}`}
                      >
                        {q}
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--text-dim)] mt-8">
                  or attach a file — resume, photo, voice memo, anything.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-8 md:gap-10 pb-4">
              {messages.map((m, i) => (
                m.deep ? (
                  <DeepAnalysisMessage
                    key={i}
                    deep={m.deep}
                    testId={`message-${i}`}
                    onShare={
                      m.deep.stage === "done"
                        ? (flatText) => handleShare(flatText, i)
                        : undefined
                    }
                  />
                ) : m.dynamicHbm ? (
                  <ExtendedThinkingChatMessage
                    key={i}
                    output={m.dynamicHbm}
                    content={m.content}
                    testId={`message-${i}`}
                    onShare={(shareContent) => handleShare(shareContent, i)}
                  />
                ) : (
                  <ChatMessage
                    key={i}
                    role={m.role}
                    content={m.content}
                    attachments={m.attachments}
                    testId={`message-${i}`}
                    convId={activeId}
                    msgIndex={i - 1}
                    canStar={m.role === "assistant" && !!user && !!activeId && i > 0}
                    onShare={m.role === "assistant" ? (content) => handleShare(content, i) : undefined}
                    onEdit={m.role === "user" && !isBusy ? () => handleEditMessage(i) : undefined}
                    onContinue={
                      m.role === "assistant" && i === messages.length - 1 && !isBusy
                        ? handleContinue
                        : undefined
                    }
                  />
                )
              ))}

              {status === "loading" && <TypingIndicator />}
              {status === "streaming" && (
                <ChatMessage
                  role="assistant"
                  content={streamingText}
                  streaming
                  testId="message-streaming"
                />
              )}
            </div>
          )}
        </div>
      </main>

      {/* Input area */}
      <footer className="shrink-0 border-t border-[var(--border)] bg-[var(--bg)] z-[70] relative">
        <form
          onSubmit={onSubmit}
          className="max-w-[760px] mx-auto px-4 md:px-6 pt-3 md:pt-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:pb-4"
          data-testid="chat-form"
        >
          {/* Pending attachments tray */}
          {(pendingFiles.length > 0 || uploadingCount > 0) && (
            <div className="mb-2 flex flex-wrap gap-2" data-testid="pending-attachments">
              {pendingFiles.map((att, i) => (
                <AttachmentChip
                  key={i}
                  attachment={att}
                  variant="pending"
                  onRemove={() => removePendingFile(i)}
                  testId={`pending-attachment-${i}`}
                />
              ))}
              {uploadingCount > 0 && (
                <div
                  className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-[var(--bg-pill)] border border-[var(--border-soft)] text-[12px] font-mono-ui text-[var(--text-muted)]"
                  data-testid="upload-progress"
                >
                  <Loader2 size={12} className="animate-spin" />
                  Uploading {uploadingCount}…
                </div>
              )}
            </div>
          )}

          {/* Two-row composer: textarea spans full width on row 1 so long
              prompts don't get squeezed by the mode chip / send button. Row 2
              holds the attach button (left) and the mode selector + send /
              stop (right). This is the Fable/Claude/ChatGPT-style layout. */}
          <div className="flex flex-col gap-2 bg-white border border-[var(--border)] focus-within:ring-2 focus-within:ring-white/60 transition-colors rounded-2xl px-4 pt-3 pb-2 shadow-lg">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={onPasteInput}
              placeholder={messages.length === 0 ? "Ask anything…" : "Reply…"}
              rows={1}
              className="w-full bg-transparent border-none outline-none focus:ring-0 font-ui text-[15px] md:text-base placeholder:text-[var(--accent)]/40 text-[var(--accent)] caret-[var(--accent)] max-h-[200px]"
              style={{
                height: "auto",
                minHeight: "28px",
              }}
              onInput={(e) => {
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
              }}
              data-testid="chat-input"
              autoFocus
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onPickFiles}
                disabled={isBusy || pendingFiles.length >= MAX_ATTACHMENTS}
                className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-[var(--accent)]/70 hover:text-[var(--accent)] hover:bg-[var(--accent)]/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="chat-attach"
                aria-label="Attach file"
                title="Attach file"
              >
                <Paperclip size={17} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_ATTR}
                multiple
                className="hidden"
                onChange={onFilesChosen}
                data-testid="chat-file-input"
              />
              {isVoiceEnabledFor(user) && (
                <VoiceInputButton
                  disabled={isBusy}
                  onTranscript={(text) => {
                    // Append to whatever the user already typed (with a
                    // space if there's existing text) so voice adds to
                    // the composer rather than clobbering it.
                    setInput((prev) => (prev ? `${prev.trimEnd()} ${text}` : text));
                    // Refocus + grow the textarea to fit the new content.
                    requestAnimationFrame(() => {
                      const el = inputRef.current;
                      if (el) {
                        el.focus();
                        el.style.height = "auto";
                        el.style.height = Math.min(el.scrollHeight, 200) + "px";
                      }
                    });
                  }}
                />
              )}
              <div className="flex-1" />
              {isStreaming ? (
                <button
                  type="button"
                  onClick={handleStop}
                  className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-[var(--accent)] hover:bg-[var(--accent-deep)] text-white transition-colors"
                  data-testid="chat-stop"
                  aria-label="Stop generating"
                  title="Stop generating"
                >
                  <Square size={13} strokeWidth={2.5} fill="currentColor" />
                </button>
              ) : (
                <>
                  <div ref={modeMenuRef} className="relative shrink-0">
                    <button
                      type="button"
                      onClick={handleModeToggle}
                      disabled={isBusy}
                      className="h-9 max-w-[46vw] sm:max-w-none rounded-full bg-[var(--accent)]/10 hover:bg-[var(--accent)]/20 text-[var(--accent)] border border-[var(--accent)]/35 pl-3 pr-2 inline-flex items-center gap-1.5 focus:outline-none focus:ring-1 focus:ring-[var(--accent)]/40 disabled:opacity-40 transition-colors"
                      data-testid="chat-mode"
                      aria-label="Chat mode"
                      aria-haspopup="listbox"
                      aria-expanded={modeMenuOpen}
                      title={`Mode: ${activeMode.label}`}
                    >
                      <ActiveModeIcon size={13} strokeWidth={2.4} className="shrink-0" />
                      <span className="min-w-0 truncate font-mono-ui text-[10px] tracking-[0.18em] uppercase">
                        <span className="hidden sm:inline">{activeMode.label}</span>
                        <span className="sm:hidden">{activeMode.shortLabel}</span>
                      </span>
                      <ChevronDown size={12} strokeWidth={2.5} className={`shrink-0 transition-transform ${modeMenuOpen ? "rotate-180" : ""}`} />
                    </button>
                    {modeMenuOpen && (
                      <div
                        role="listbox"
                        className="absolute bottom-[calc(100%+10px)] right-0 z-[80] w-[min(86vw,22rem)] overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-2xl"
                        data-testid="chat-mode-menu"
                      >
                        <div className="px-3 py-2 border-b border-[var(--border)] font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
                          Choose mode
                        </div>
                        {modeOptions.map((opt) => {
                          const Icon = opt.icon;
                          const active = chatMode === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              role="option"
                              aria-selected={active}
                              onClick={handleModeSelect(opt.value)}
                              className={`w-full text-left px-3 py-3 flex items-center gap-3 transition-colors ${
                                active ? "bg-[var(--accent)] text-white" : "bg-transparent text-[var(--text)] hover:bg-[var(--text)]/5"
                              }`}
                              data-testid={`chat-mode-option-${opt.value}`}
                            >
                              <span className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 ${active ? "border-white/60 bg-white/10" : "border-[var(--border)] bg-[var(--bg)]"}`}>
                                <Icon size={14} strokeWidth={2.4} />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block font-mono-ui text-[11px] tracking-[0.18em] uppercase truncate">{opt.label}</span>
                                <span className={`block font-ui text-xs mt-0.5 ${active ? "text-white/75" : "text-[var(--text-muted)]"}`}>{opt.description}</span>
                              </span>
                              <Check size={13} strokeWidth={3} className={`shrink-0 ${active ? "opacity-100" : "opacity-0"}`} />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={!canSend}
                    className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-[var(--accent)] hover:bg-[var(--accent-deep)] text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="chat-send"
                    aria-label="Send"
                  >
                    <ArrowUp size={18} strokeWidth={2.5} />
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
      </footer>
      </div>

      <HistoryDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        conversations={conversations}
        activeId={activeId}
        onSelect={loadConversation}
        onDelete={onDeleteConv}
        onClear={onClearAll}
        onNew={startNewChat}
        user={user}
        onLogout={logout}
        memoryEnabled={!!(user && user.memory_enabled)}
        onToggleMemory={handleToggleMemory}
        projects={projects}
        projectsAvailable={projectsAvailable}
        activeProjectId={activeProjectId}
        onSelectProject={handleSelectProject}
        onCreateProject={handleCreateProjectClick}
        onEditProject={handleEditProjectClick}
        onDeleteProject={handleDeleteProjectClick}
      />

      <ProjectModal
        open={projectModalOpen}
        project={projectModalTarget}
        onClose={() => {
          if (!projectModalBusy) {
            setProjectModalOpen(false);
            setProjectModalTarget(null);
          }
        }}
        onSave={handleSaveProject}
        busy={projectModalBusy}
      />

      <ShareCardModal
        open={!!shareTarget}
        onClose={() => setShareTarget(null)}
        question={shareTarget?.question || ""}
        answer={shareTarget?.answer || ""}
      />

      <SignupWallModal
        open={signupWallOpen}
        used={anonQuota?.used ?? 5}
        limit={anonQuota?.limit ?? 5}
        onClose={() => setSignupWallOpen(false)}
      />

      <ImportHistoryModal
        open={importModalOpen}
        count={loadConversations().length}
        onImport={handleImportConfirm}
        onSkip={handleImportSkip}
        busy={importBusy}
      />

      {/* Deep Analysis upsell — shown when a free/anon user tries to use
          the Deep mode. Simple, informational; no purchase flow yet.
          When you wire Stripe, replace onClose with the checkout redirect. */}
      {deepUpsellOpen && (
        <div
          className="fixed inset-0 z-[80] bg-[var(--text)]/70 flex items-center justify-center px-4"
          onClick={() => setDeepUpsellOpen(false)}
          data-testid="deep-upsell"
        >
          <div
            className="max-w-md w-full bg-[var(--bg-elevated)] border border-[var(--border)] rounded-lg p-6 text-[var(--text)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]/60 mb-2">
              Admin only
            </div>
            <h3 className="font-prose text-2xl mb-3">Deep Analysis is admin-only for now</h3>
            <p className="text-[var(--text)]/85 leading-relaxed mb-6">
              Deep Analysis breaks your prompt into five sharp sub-questions,
              answers each in the HBM voice, and weaves them into a single
              structural read. Currently restricted to designated admin
              accounts — public access will open soon.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeepUpsellOpen(false)}
                className="flex-1 py-2.5 rounded-full border border-[var(--border)] hover:bg-[var(--text)]/10 font-mono-ui text-[10px] tracking-[0.2em] uppercase transition-colors"
                data-testid="deep-upsell-close"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setDeepUpsellOpen(false);
                  setChatMode("standard");
                }}
                className="flex-1 py-2.5 rounded-full bg-[var(--accent)] text-white hover:opacity-90 font-mono-ui text-[10px] tracking-[0.2em] uppercase transition-colors"
                data-testid="deep-upsell-switch-standard"
              >
                Use Standard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExtendedThinkingChatMessage({ output, content, testId, onShare }) {
  const state = output?.structured_state || {};
  const delta = output?.turn_delta || {};
  const provider = output?.provider_debug || {};
  const modules = output?.active_reasoning_modules || [];
  const movements = output?.hypothesis_movement || [];
  const links = output?.evidence_links || [];
  const answer = output?.final_response || content || "Extended Thinking returned no answer.";
  const shareText = [
    "FINAL HBM ANSWER",
    answer,
    "",
    "TURN DELTA",
    delta.summary || "No material state change captured.",
    "",
    "ACTIVE MODULES",
    modules.map((m) => `- ${m.label || m.id}: ${m.reason || "selected"}`).join("\n") || "None",
    "",
    "NEXT DIAGNOSTIC QUESTION",
    output?.next_diagnostic_question?.text || "None",
  ].join("\n");

  return (
    <div className="flex justify-start fade-up" data-testid={testId}>
      <div className="max-w-full md:max-w-[92%] w-full space-y-4">
        <section data-testid={`${testId}-extended-final-answer`} className="border-2 border-[var(--accent)] p-5 bg-[var(--bg-glow)]">
          <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--accent)] inline-flex items-center gap-2 mb-3">
            <BrainCircuit size={14} /> Extended Thinking · Final HBM Answer
          </div>
          <p className="font-prose text-[19px] md:text-[22px] leading-[1.55] whitespace-pre-wrap text-[var(--text)]">
            {answer}
          </p>
        </section>

        <section data-testid={`${testId}-extended-turn-delta`} className="border border-[var(--border)] p-4">
          <MiniExtendedTitle icon={<GitBranch size={13} />} title="Turn delta" />
          <p className="font-ui text-sm leading-relaxed text-[var(--text)] mb-3">{delta.summary || "No material state change captured."}</p>
          <div className="grid sm:grid-cols-3 gap-2">
            <ExtendedStat testid={`${testId}-extended-stage-before`} label="Before" value={delta.stage_before || "—"} />
            <ExtendedStat testid={`${testId}-extended-stage-after`} label="After" value={delta.stage_after || "—"} />
            <ExtendedStat testid={`${testId}-extended-act`} label="Act" value={delta.discourse_act || "—"} />
          </div>
        </section>

        <section data-testid={`${testId}-extended-inference-request`} className="border border-[var(--border)] p-4">
          <MiniExtendedTitle icon={<BrainCircuit size={13} />} title="Inference request" />
          <div className="grid sm:grid-cols-4 gap-2 mb-3">
            <ExtendedStat testid={`${testId}-extended-provider`} label="Provider" value={provider.provider || provider.engine || "—"} />
            <ExtendedStat testid={`${testId}-extended-model`} label="Model" value={provider.model || "—"} />
            <ExtendedStat testid={`${testId}-extended-status`} label="Status" value={provider.status || "—"} />
            <ExtendedStat testid={`${testId}-extended-latency`} label="Latency" value={provider.latency_ms ? `${provider.latency_ms}ms` : "—"} />
          </div>
          <ExtendedList
            testid={`${testId}-extended-provider-diagnostics`}
            empty="No provider diagnostics returned."
            items={(provider.diagnostics || []).map((d, idx) => ({ id: `D-${idx + 1}`, title: d }))}
          />
          {provider.validation?.warnings?.length > 0 && (
            <p data-testid={`${testId}-extended-validation-warning`} className="font-ui text-sm text-[var(--danger)] mt-3">
              Validation warnings: {provider.validation.warnings.join(", ")}
            </p>
          )}
        </section>

        <section data-testid={`${testId}-extended-modules`} className="border border-[var(--border)] p-4">
          <MiniExtendedTitle icon={<Layers3 size={13} />} title="Active modules" />
          <ExtendedList
            testid={`${testId}-extended-module-list`}
            empty="No temporary module was active."
            items={modules.map((m) => ({ id: m.id, title: m.label || m.id, meta: m.reason }))}
          />
        </section>

        <section data-testid={`${testId}-extended-movement`} className="border border-[var(--border)] p-4">
          <MiniExtendedTitle icon={<GitBranch size={13} />} title="Hypothesis movement" />
          <ExtendedList
            testid={`${testId}-extended-movement-list`}
            empty="No hypothesis changed this turn."
            items={movements.map((h) => ({
              id: h.id,
              title: h.claim,
              meta: `${h.direction} · ${h.status_before || "new"} → ${h.status_after}`,
            }))}
          />
        </section>

        <section data-testid={`${testId}-extended-evidence`} className="border border-[var(--border)] p-4">
          <MiniExtendedTitle icon={<GitBranch size={13} />} title="Evidence links" />
          <ExtendedList
            testid={`${testId}-extended-evidence-list`}
            empty="No explicit evidence links yet."
            items={links.slice(0, 8).map((l, idx) => ({
              id: `${l.hypothesis_id}-${l.observation_id}-${idx}`,
              title: `${l.observation_id} ${l.relation} ${l.hypothesis_id}`,
              meta: l.observation,
            }))}
          />
        </section>

        <section data-testid={`${testId}-extended-next-question`} className="border border-[var(--border)] p-4">
          <MiniExtendedTitle icon={<HelpCircle size={13} />} title="Next diagnostic question" />
          <p className="font-ui text-sm leading-relaxed text-[var(--text)]">
            {output?.next_diagnostic_question?.text || "No open diagnostic question is currently necessary."}
          </p>
        </section>

        <details data-testid={`${testId}-extended-structured-state`} className="border border-[var(--border)] p-4" open>
          <summary data-testid={`${testId}-extended-structured-state-toggle`} className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--accent)] cursor-pointer">
            Structured case state · O/I/H/A/C/J/Q
          </summary>
          <div className="grid gap-3 mt-4">
            <ExtendedBucket testid={`${testId}-extended-observations`} title="O-* Observations" items={state.observations} render={(o) => ({ id: o.id, title: o.text, meta: `${o.status} · confidence ${o.confidence}` })} />
            <ExtendedBucket testid={`${testId}-extended-interpretations`} title="I-* Interpretations" items={state.interpretations} render={(i) => ({ id: i.id, title: i.text, meta: i.source })} />
            <ExtendedBucket testid={`${testId}-extended-hypotheses`} title="H-* Hypotheses" items={state.hypotheses} render={(h) => ({ id: h.id, title: h.claim, meta: `${h.origin} · ${h.status}` })} />
            <ExtendedBucket testid={`${testId}-extended-claims`} title="A-* HBM claims" items={state.assistant_claims} render={(a) => ({ id: a.id, title: a.text, meta: `${a.claim_type} · ${a.status}` })} />
            <ExtendedBucket testid={`${testId}-extended-contradictions`} title="C-* Contradictions" items={state.contradictions} render={(c) => ({ id: c.id, title: c.description, meta: c.status })} />
            <ExtendedBucket testid={`${testId}-extended-judgements`} title="J-* Judgements / rejections" items={state.judgements} render={(j) => ({ id: j.id, title: j.reason, meta: `${j.hypothesis_id} · ceiling ${j.ceiling}` })} />
            <ExtendedBucket testid={`${testId}-extended-questions`} title="Q-* Unresolved questions" items={state.unresolved_questions} render={(q) => ({ id: q.id, title: q.text, meta: `priority ${q.priority}` })} />
          </div>
        </details>

        <div className="flex flex-wrap items-center gap-2" data-testid={`${testId}-extended-actions`}>
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(shareText).then(() => toast.success("Copied"))}
            data-testid={`${testId}-extended-copy`}
            className="font-mono-ui text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border)] bg-[var(--text)]/10 text-[var(--text)] hover:bg-white hover:text-[var(--accent)] transition-colors"
          >
            <Check size={12} /> Copy output
          </button>
          {onShare && (
            <button
              type="button"
              onClick={() => onShare(shareText)}
              data-testid={`${testId}-extended-share`}
              className="font-mono-ui text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--border)] bg-[var(--text)]/10 text-[var(--text)] hover:bg-white hover:text-[var(--accent)] transition-colors"
            >
              Share
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniExtendedTitle({ icon, title }) {
  return (
    <div className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[var(--accent)] inline-flex items-center gap-2 mb-3">
      {icon}
      {title}
    </div>
  );
}

function ExtendedStat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-[var(--border)] px-3 py-2 min-w-0">
      <div className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--text-dim)]">{label}</div>
      <div className="font-mono-ui text-xs text-[var(--text)] mt-1 truncate">{String(value ?? "—")}</div>
    </div>
  );
}

function ExtendedList({ items, empty, testid }) {
  if (!items || items.length === 0) {
    return <p data-testid={`${testid}-empty`} className="font-ui text-sm text-[var(--text-muted)]">{empty}</p>;
  }
  return (
    <div data-testid={testid} className="space-y-2">
      {items.map((item) => (
        <div key={item.id} data-testid={`${testid}-item-${item.id}`} className="border-l-2 border-[var(--border)] pl-3 py-1">
          <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text)] break-all">{item.id}</div>
          <p className="font-ui text-sm text-[var(--text)] leading-relaxed mt-1 whitespace-pre-wrap">{item.title || "—"}</p>
          {item.meta && <p className="font-mono-ui text-[9px] tracking-[0.16em] uppercase text-[var(--text-dim)] mt-1 break-all">{item.meta}</p>}
        </div>
      ))}
    </div>
  );
}

function ExtendedBucket({ title, items, render, testid }) {
  const rows = items || [];
  return (
    <div data-testid={testid}>
      <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] mb-2">
        {title} · {rows.length}
      </div>
      <ExtendedList
        testid={`${testid}-list`}
        empty={`No ${title.toLowerCase()} yet.`}
        items={rows.map(render)}
      />
    </div>
  );
}
